import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, Subscription } from "rxjs";
import { showToast } from "../../toast.helper";
import { ClassificationIndicator, ClassificationStatus, ManagersNotes } from "../models/classification-indicators.model";
import { Evaluator } from 'formiojs/utils/utils';
import _cloneDeep from 'lodash/cloneDeep';
import { SortHelper } from "../../../helpers/sort.helper";
import { replacer } from "../../../helpers/json.helper";
import * as DeleteConfirmationDialog from 'dp-formio';
import { MatMenuTrigger } from "@angular/material/menu";
import { ResourceNameService } from "dp-formio";

declare var $: any;

@Component({
    selector: 'app-classification-comments',
    templateUrl: './classification-comments.component.html',
    // styleUrls: ['./classification-comments.component.scss']
    encapsulation: ViewEncapsulation.None
})
export class ClassificationCommentsComponent implements OnInit, OnDestroy {
    @Input() data: any;
    @Input() indicator: ClassificationIndicator;
    @Input() dir: 'rtl' | 'ltr';
    @Input() domainId: string;
    @Input() questionId: string;
    @Output() onClose = new EventEmitter();
    @Output() onHistoryUpdated = new EventEmitter();
    @ViewChild('messageElement') messageElement: ElementRef;
    @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;

    message = '';
    managersNotes: ManagersNotes[] = [];
    loading = false;
    hasMessageFromSameTask = false;
    editing: ManagersNotes;
    deleteComment = false;

    limit = 10;
    skip = 0;
    totalRecords = 0;
    lastScrollHeight = 0;

    private subs: Subscription[] = [];
    set sub(s: Subscription) { this.subs.push(s); }

    get taskId() {
        return this.data.formData?.taskId || this.data.formData?.taskResource?.data?.taskId;
    }

    constructor(private toastrService: ToastrService,
        private resourceNameService: ResourceNameService,
        private translateService: TranslateService) {
    }
    ngOnInit(): void {
        this.loadData();
    }

    inputKeyup() {
        this.messageElement.nativeElement.style.height = this.calcHeight(this.messageElement.nativeElement.value) + "px";
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
        this.subs = [];
    }

    close() {
        this.onClose.emit();
    }

    send() {
        if (!this.message || this.loading || (this.hasMessageFromSameTask && !this.editing)) return;
        if (this.editing) {
            this.updateComment();
            return;
        }

        const comment = new ManagersNotes();
        comment.questionSubmissionId = this.questionId;
        comment.requestId = this.data.formData?.requestId || this.data.formData?.requestDetails?.data?.varRequestId;
        comment.taskId = this.taskId;
        comment.userName = this.data.formData?.currentUser_preferred_username;
        comment.givenName = this.data.formData?.currentUser_name;
        comment.email = this.data.formData?.currentUser_email;
        comment.role = this.data.formData?.currentUser_roles?.join();
        comment.roleByTask = this.getRoleBytask();
        comment.message = this.message;
        delete comment._id;
        delete comment.fromMe;

        const history = this.initHistoryMap(this.indicator._id, this.domainId, this.questionId, this.taskId);
        const calls = [
            this.resourceNameService.addSubmission(this.data.customOptions.managersNotes, { data: comment }),
            this.resourceNameService.
                patchSubmission(this.data.customOptions.indicatorResource,
                    this.indicator._id,
                    [{

                        "op": "add",
                        "path": "/data/history",
                        "value": JSON.stringify(history, replacer)
                    }
                    ])
        ];

        this.loading = true;
        this.sub = forkJoin(calls)
            .subscribe((data: any[]) => {
                this.message = '';
                this.resetInput();
                comment._id = data[0]._id;
                comment.created = data[0].created;
                comment.fromMe = true;
                this.managersNotes.push(comment);
                this.hasMessageFromSameTask = true;
                this.indicator.history = history;
                this.onHistoryUpdated.emit();

                this.scrollToBottom();
                this.loading = false;
                setTimeout(() => { this.messageElement.nativeElement.focus(); });
            }, error => {
                this.loading = false;
                showToast(this.toastrService,
                    this.translateService.instant("ErrorOccurred"),
                    this.translateService.instant("generalError"),
                    true
                );
            });
    }

    edit(item: ManagersNotes, event) {
        event.stopPropagation();
        this.menuTrigger.closeMenu();
        this.editing = _cloneDeep(item);
        this.message = this.editing.message;
    }

    delete(index: number, item: ManagersNotes, event) {
        event.stopPropagation();
        this.menuTrigger.closeMenu();
        DeleteConfirmationDialog.build(this.data.formioComponent, () => {
            const history = this.removeCommentFromHistoryMap(this.indicator._id, this.domainId, this.questionId);
            const calls = [
                this.resourceNameService.deleteResourceData(this.data.customOptions.managersNotes, item._id),
                this.resourceNameService.
                    patchSubmission(this.data.customOptions.indicatorResource,
                        this.indicator._id,
                        [{

                            "op": "add",
                            "path": "/data/history",
                            "value": JSON.stringify(history, replacer)
                        }
                        ])
            ];

            this.loading = true;
            this.sub = forkJoin(calls)
                .subscribe((data: any[]) => {
                    this.deleteComment = true;
                    this.managersNotes.splice(index, 1);
                    this.indicator.history = history;
                    this.hasMessageFromSameTask = false;
                    this.onHistoryUpdated.emit();
                    this.loading = false;
                    setTimeout(() => { this.messageElement.nativeElement.focus(); });
                }, error => {
                    this.loading = false;
                    showToast(this.toastrService,
                        this.translateService.instant("ErrorOccurred"),
                        this.translateService.instant("generalError"),
                        true
                    );
                });
        }, null, 'Are you sure to delete this item');
    }

    onScroll(event) {
        if (!this.deleteComment && event.target.scrollTop == 0 && this.managersNotes.length < this.totalRecords) {
            this.skip += 10;
            this.loadData(true);
        }
        this.deleteComment = false;
    }

    trackByFn(index: number, comment: ManagersNotes): string {
        return comment._id;
    }

    private updateComment() {
        this.loading = true;
        this.sub = this.resourceNameService.patchSubmission(this.data.customOptions.managersNotes,
            this.editing._id, [{

                "op": "add",
                "path": "/data/message",
                "value": this.editing.message
            }]).subscribe((data: any[]) => {
                this.managersNotes.find(q => q._id == this.editing._id).message = this.message;
                this.message = '';
                this.editing = null;

                this.scrollToBottom();
                this.loading = false;
                setTimeout(() => { this.messageElement.nativeElement.focus(); });
            }, error => {
                this.loading = false;
                showToast(this.toastrService,
                    this.translateService.instant("ErrorOccurred"),
                    this.translateService.instant("generalError"),
                    true
                );
            });
    }

    private loadData(loadMore: boolean = false) {
        this.loading = true;
        const submission = _cloneDeep(this.data.submission);
        submission.questionSubmissionId = this.questionId;
        const query = Evaluator.interpolate(this.data.customOptions.managersNotesQuery, { data: submission }) + `&sort=-created&skip=${this.skip}&limit=${this.limit}`;
        this.sub = this.resourceNameService.loadPage(this.data.customOptions.managersNotes, query)
            .subscribe(data => {
                if (loadMore) {
                    const newCommets = data.data.map(q => {
                        q = { ...q.data, created: q.created, _id: q._id };
                        const message = new ManagersNotes();
                        SortHelper.copyProps(q, message);
                        message.fromMe = message.email == this.data.formData?.currentUser_email
                        return message;
                    });
                    this.managersNotes = newCommets.reverse().concat(this.managersNotes);
                    this.scrollToLastComment();
                } else {
                    this.totalRecords = data.totalRecords;
                    this.managersNotes = data.data.map(q => {
                        q = { ...q.data, created: q.created, _id: q._id };
                        const message = new ManagersNotes();
                        SortHelper.copyProps(q, message);
                        message.fromMe = message.email == this.data.formData?.currentUser_email
                        return message;
                    }).reverse();
                    this.hasMessageFromSameTask = this.managersNotes.some(q => q.taskId == this.taskId);
                    this.scrollToBottom();
                }
                this.loading = false;
            }, error => {
                this.loading = false;
                showToast(this.toastrService,
                    this.translateService.instant("ErrorOccurred"),
                    this.translateService.instant("generalError"),
                    true
                );
            });
    }

    private scrollToLastComment() {
        setTimeout(() => {
            const container = $('.comments-list-container')[0];
            const lastCommentPosition = container.scrollHeight - this.lastScrollHeight;
            this.lastScrollHeight = container.scrollHeight;
            container.scrollTo({ behavior: 'smooth', top: lastCommentPosition });
        });
    }

    private scrollToBottom() {
        setTimeout(() => {
            const container = $('.comments-list-container')[0];
            this.lastScrollHeight = container.scrollHeight;
            container.scrollTo({ behavior: 'smooth', top: container.scrollHeight });
        });
    }

    private initHistoryMap(indicatorId: string, domainId: string, questionId: string, taskId: string) {
        const indicator = _cloneDeep(this.indicator);
        if (!indicator.history.has(indicatorId))
            indicator.history.set(indicatorId, new Map());

        if (!indicator.history.get(indicatorId).has(domainId))
            indicator.history.get(indicatorId).set(domainId, new Map());

        if (!indicator.history.get(indicatorId).get(domainId).has(questionId))
            indicator.history.get(indicatorId).get(domainId).set(questionId, taskId);
        return indicator.history;
    }

    private removeCommentFromHistoryMap(indicatorId: string, domainId: string, questionId: string) {
        const indicator = _cloneDeep(this.indicator);
        indicator.history.get(indicatorId).get(domainId).delete(questionId);
        if (!indicator.history.get(indicatorId).get(domainId).size) {
            indicator.history.get(indicatorId).delete(domainId);
            if (!indicator.history.get(indicatorId).size) {
                indicator.history.delete(indicatorId);
            }
        }
        return indicator.history;
    }

    private getRoleBytask() {
        let roleByTask = null;
        const currentRoute = this.data.formData?.currentRoute || this.data.formData?.requestDetails?.data?.currentRoute;
        if (currentRoute.includes('requests/task/ClassificationDataEntry') || currentRoute.includes('requests/task/classificationRequestUpdateCertificationData'))
            roleByTask = 'Classification-DataEntry';
        else if (currentRoute.includes('requests/task/ClassificationDataReview'))
            roleByTask = 'Classification-Auditor';
        else if (currentRoute.includes('requests/task/certificateClassification'))
            roleByTask = 'Classification-Commissioner';
        else if (currentRoute.includes('ClassificationRequestDetectiveReview/ClassificationRequest'))
            roleByTask = 'Classification-Detective';
        else if (currentRoute.includes('ClassificationRequestSpecialistReview/ClassificationRequest'))
            roleByTask = 'Classification-Specialist';
        return roleByTask;
    }

    private calcHeight(value) {
        let numberOfLineBreaks = (value.match(/\n/g) || []).length;
        // min-height + lines x line-height + padding + border
        let newHeight = 20 + numberOfLineBreaks * 20 + 12 + 2;
        return newHeight;
    }

    private resetInput() {
        this.messageElement.nativeElement.style.height = this.calcHeight('') + "px";
    }
}
